import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Send,
  Cancel,
  CheckCircle,
  HighlightOff,
  SwapHoriz,
} from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, Vendor } from '../../services/procurementService';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';
import { RequisitionPriority, RequisitionStatus, RequisitionType } from './PurchaseRequisitionPage';

// ---------------------------------------------------------------------------
// Types -- raw snake_case shape from GET /purchase-requisitions/:id
// ---------------------------------------------------------------------------

interface RequisitionItem {
  id: number;
  product_id: string;
  product_name?: string;
  product_code?: string;
  product_uom?: string;
  hsn_code?: string;
  quantity: number | string;
  uom?: string | null;
  estimated_unit_cost: number | string;
  estimated_total_cost: number | string;
  specification?: string | null;
  preferred_vendor_id?: string | null;
  preferred_vendor_name?: string | null;
}

interface RequisitionDetail {
  id: number;
  requisition_number: string;
  requisition_date: string;
  requisition_type: RequisitionType;
  requested_by_name?: string | null;
  department: string | null;
  cost_center_name?: string | null;
  cost_center_code?: string | null;
  project_name?: string | null;
  warehouse_name?: string | null;
  required_by_date: string | null;
  priority: RequisitionPriority;
  status: RequisitionStatus;
  total_estimated_cost: number | string;
  justification: string | null;
  approved_by_name?: string | null;
  approved_date?: string | null;
  converted_po_id?: number | null;
  converted_po_number?: string | null;
  created_at: string;
  items: RequisitionItem[];
}

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'CONVERTED_TO_PO', label: 'Converted to PO' },
];

/** Where a live status sits on the 4-step happy-path ribbon. UNDER_REVIEW
 * shares the "Submitted" slot -- it is mid-review, not a separate stage. */
const FLOW_INDEX: Partial<Record<RequisitionStatus, number>> = {
  DRAFT: 0,
  SUBMITTED: 1,
  UNDER_REVIEW: 1,
  APPROVED: 2,
  CONVERTED_TO_PO: 3,
};

const STATUS_TONE: Record<RequisitionStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  UNDER_REVIEW: 'warning',
  APPROVED: 'success',
  CONVERTED_TO_PO: 'info',
  REJECTED: 'error',
  RETURNED: 'warning',
  CANCELLED: 'neutral',
};

const PRIORITY_COLOR: Record<RequisitionPriority, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');
const fmtDateTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-IN') : '—');
const todayISO = () => new Date().toISOString().slice(0, 10);

const PurchaseRequisitionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [req, setReq] = useState<RequisitionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Dialog state
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  // Convert-to-PO form
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [convertVendor, setConvertVendor] = useState<Vendor | null>(null);
  const [poDate, setPoDate] = useState(todayISO());
  const [deliveryDate, setDeliveryDate] = useState('');
  const [gstRate, setGstRate] = useState('18');
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { requisition } = await api.get<{ requisition: RequisitionDetail }>(`/purchase-requisitions/${id}`);
      setReq(requisition);
    } catch (err: any) {
      setError(err.message || 'Failed to load purchase requisition');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    procurementService
      .getVendors()
      .then((r) => setVendors((r.vendors || []).filter((v) => v.is_active !== false)))
      .catch(() => {});
  }, []);

  // Pre-select the vendor when opening the convert dialog if every line
  // agrees on one preferred vendor -- mirrors the backend's own
  // single-preferred-vendor auto-resolution in convertToPo().
  const openConvert = () => {
    if (!req) return;
    const preferredIds = Array.from(
      new Set((req.items || []).map((i) => i.preferred_vendor_id).filter(Boolean))
    );
    const single =
      preferredIds.length === 1 ? vendors.find((v) => String(v.id) === String(preferredIds[0])) || null : null;
    setConvertVendor(single);
    setPoDate(todayISO());
    setDeliveryDate(req.required_by_date ? req.required_by_date.slice(0, 10) : '');
    setGstRate('18');
    setConvertError(null);
    setConvertOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !req) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/requisitions')} sx={{ mb: 2 }}>
          Back to Requisitions
        </Button>
        <Alert severity="error">{error || 'Requisition not found'}</Alert>
      </Box>
    );
  }

  const isSideState = (['REJECTED', 'RETURNED', 'CANCELLED'] as RequisitionStatus[]).includes(req.status);
  const flowIndex = FLOW_INDEX[req.status] ?? 0;

  const canEdit = req.status === 'DRAFT' || req.status === 'RETURNED';
  const canSubmit = req.status === 'DRAFT' || req.status === 'RETURNED';
  const canCancel = !(['CONVERTED_TO_PO', 'CANCELLED', 'REJECTED'] as RequisitionStatus[]).includes(req.status);
  const canApproveReject = req.status === 'SUBMITTED' || req.status === 'UNDER_REVIEW';
  const canConvert = req.status === 'APPROVED';

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = () =>
    runAction('submit', async () => {
      await api.post(`/purchase-requisitions/${id}/submit`);
      setNotice('Requisition submitted for approval.');
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      await api.post(`/purchase-requisitions/${id}/approve`, { comment: approveComment.trim() || undefined });
      setApproveOpen(false);
      setApproveComment('');
      setNotice('Requisition approved.');
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await api.post(`/purchase-requisitions/${id}/reject`, { reason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      setNotice('Requisition rejected.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await api.post(`/purchase-requisitions/${id}/cancel`);
      setCancelOpen(false);
      setNotice('Requisition cancelled.');
      await load();
    });

  const handleConvert = async () => {
    if (!convertVendor) {
      setConvertError('Select a vendor to raise the purchase order against.');
      return;
    }
    setConverting(true);
    setConvertError(null);
    try {
      const { requisition } = await api.post<{ requisition: RequisitionDetail }>(
        `/purchase-requisitions/${id}/convert-to-po`,
        {
          vendorId: convertVendor.id,
          poDate,
          deliveryDate: deliveryDate || undefined,
          gstRate: toNum(gstRate),
        }
      );
      if (requisition.converted_po_id) {
        navigate(`/procurement/orders/${requisition.converted_po_id}`);
      } else {
        setConvertOpen(false);
        await load();
      }
    } catch (err: any) {
      setConvertError(err.message || 'Failed to convert requisition to a purchase order');
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/requisitions')} sx={{ mb: 1 }}>
        Back to Requisitions
      </Button>

      <PageHeader
        title={req.requisition_number}
        qualifier={req.department ? `· ${req.department}` : undefined}
        subtitle={`Raised ${fmtDate(req.requisition_date)}${req.requested_by_name ? ` by ${req.requested_by_name}` : ''}`}
        actions={
          <>
            <StatusDot label={req.status.replace(/_/g, ' ')} tone={STATUS_TONE[req.status]} />
            <Chip size="small" label={req.priority} color={PRIORITY_COLOR[req.priority]} />
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity={req.status === 'CANCELLED' ? 'warning' : 'error'} icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>
                Requisition {req.status === 'RETURNED' ? 'Returned for Changes' : req.status.replace(/_/g, ' ')}
              </Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              component={RouterLink}
              to={`/procurement/requisitions/${id}/edit`}
            >
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button
              variant="contained"
              startIcon={<Send />}
              disabled={actionLoading !== null}
              onClick={handleSubmit}
            >
              {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={actionLoading !== null || req.status === 'SUBMITTED'}
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                disabled={actionLoading !== null || req.status === 'SUBMITTED'}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
              {req.status === 'SUBMITTED' && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Awaiting the approval engine to route this requisition -- refresh shortly.
                </Typography>
              )}
            </>
          )}
          {canConvert && (
            <Button variant="contained" startIcon={<SwapHoriz />} onClick={openConvert}>
              Convert to Purchase Order
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading !== null}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Requisition
            </Button>
          )}
          {!canEdit && !canSubmit && !canApproveReject && !canConvert && !canCancel && (
            <Typography variant="body2" color="text.secondary">
              This requisition is {req.status.replace(/_/g, ' ').toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
        {req.converted_po_number && req.converted_po_id && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" component={RouterLink} to={`/procurement/orders/${req.converted_po_id}`}>
              View Purchase Order {req.converted_po_number}
            </Button>
          </Box>
        )}
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Requisition Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Type', req.requisition_type.replace(/_/g, ' ')],
                ['Department', req.department],
                ['Cost Center', req.cost_center_name ? `${req.cost_center_code} -- ${req.cost_center_name}` : null],
                ['Project', req.project_name],
                ['Warehouse', req.warehouse_name],
                ['Required By', fmtDate(req.required_by_date)],
                ['Estimated Value', formatIndianNumber(toNum(req.total_estimated_cost))],
                ['Approved By', req.approved_by_name],
                ['Approved On', req.approved_date ? fmtDate(req.approved_date) : null],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Justification
            </Typography>
            <Typography variant="body2" color={req.justification ? 'text.primary' : 'text.disabled'}>
              {req.justification || 'No justification recorded.'}
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Product', 'Qty', 'UOM', 'Est. Unit Cost', 'Est. Total', 'Specification', 'Preferred Vendor'].map(
                    (h, i) => (
                      <TableCell key={h} align={i === 1 || i === 3 || i === 4 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {req.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <TableEmptyState title="No line items on this requisition" />
                    </TableCell>
                  </TableRow>
                ) : (
                  req.items.map((item) => (
                    <TableRow key={item.id} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.product_name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.product_code}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(item.quantity)}
                      </TableCell>
                      <TableCell>{item.uom || item.product_uom || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(item.estimated_unit_cost))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(item.estimated_total_cost))}
                      </TableCell>
                      <TableCell>{item.specification || '—'}</TableCell>
                      <TableCell>{item.preferred_vendor_name || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableSurface>
        </Grid>

        {/* Documents */}
        <Grid size={{ xs: 12 }}>
          <AttachmentsPanel entityType="purchase_requisition" entityId={req.id} title="Requisition Documents" />
        </Grid>
      </Grid>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Requisition</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Approving lets this requisition be converted into a purchase order.
          </DialogContentText>
          <TextField
            label="Comment (optional)"
            fullWidth
            multiline
            rows={3}
            value={approveComment}
            onChange={(e) => setApproveComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading === 'approve'}
          >
            {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Requisition</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            A reason is required and will be visible to the requester.
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectReason.trim().length < 3 || actionLoading === 'reject'}
          >
            {actionLoading === 'reject' ? 'Rejecting...' : 'Reject Requisition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Requisition</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. Any pending approval request for this requisition will also be cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Requisition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to PO */}
      <Dialog open={convertOpen} onClose={() => (converting ? undefined : setConvertOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Purchase Order</DialogTitle>
        <DialogContent dividers>
          {convertError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConvertError(null)}>
              {convertError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={vendors}
              value={convertVendor}
              onChange={(_, v) => setConvertVendor(v)}
              getOptionLabel={(o) => `${o.company_name} (${o.vendor_code})`}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              renderInput={(params) => <TextField {...params} label="Vendor *" />}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="PO Date"
                type="date"
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <TextField
                label="Delivery Date"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <TextField
                label="GST Rate %"
                type="number"
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                sx={{ flex: 1, minWidth: 120 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Every requisition line will be carried onto the new purchase order at its estimated unit cost, and can
              still be adjusted before the PO is sent.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)} disabled={converting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConvert} disabled={converting || !convertVendor}>
            {converting ? 'Converting...' : 'Create Purchase Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseRequisitionDetailPage;
